import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { Base, ReverLoading } from '@itsrever/design-system'
import {
  useHasSizingData,
  useSizingCycle,
  useSizingModels,
  useSizingOverview,
  useSizingStore
} from '@/application/queries'
import ErrorNotFound from '@/ui/pages/404.page'
import {
  OverviewTab,
  OverviewTabLoading
} from '@/ui/components/sizing/overview-tab'
import { ModelsTab } from '@/ui/components/sizing/models-tab'
import { SizingInfoButton } from '@/ui/components/sizing/info-modal'

const { Badge, TabsRoot, TabsList, TabsIndicator, Tab, TabPanel } = Base

const OVERVIEW = 'overview'
const MODELS = 'models'

type TabsValue = string

const SizingIcon = () => (
  <div className="flex select-none items-center justify-center rounded-full border border-solid border-neutral-200 bg-white p-3 shadow-sm">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={22}
      height={22}
      stroke="currentColor"
      fill="none"
      strokeWidth="1.75"
      viewBox="0 0 24 24"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-neutral-700"
      aria-hidden
    >
      <path d="M19.875 6.27a1.34 1.34 0 0 1 .625 1.132v9.196a1.35 1.35 0 0 1 -.625 1.133l-7.25 4.24a1.32 1.32 0 0 1 -1.25 0l-7.25 -4.24a1.34 1.34 0 0 1 -.625 -1.132v-9.197a1.35 1.35 0 0 1 .625 -1.132l7.25 -4.24a1.32 1.32 0 0 1 1.25 0z" />
      <path d="M12 12l8 -4.5" />
      <path d="M12 12v9" />
      <path d="M12 12l-8 -4.5" />
    </svg>
  </div>
)

const SizingPage = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  const { storeId, settingsLoaded } = useSizingStore()

  const { data: hasSizingData } = useHasSizingData(storeId)
  const { data: overview, status: overviewStatus } = useSizingOverview(storeId)
  const { data: models, status: modelsStatus } = useSizingModels(storeId)
  const { data: cycle } = useSizingCycle(storeId)

  if (!settingsLoaded) {
    return (
      <div className="flex h-full items-center justify-center">
        <ReverLoading />
      </div>
    )
  }
  if (!storeId || hasSizingData === false) {
    return <ErrorNotFound />
  }

  const currentTab = searchParams.get('tab') ?? OVERVIEW
  const modelCount = models?.models.length ?? 0
  const loadError = (
    <Base.EmptyState
      variant="card"
      title={t('sizing.error_title')}
      description={t('sizing.error_description')}
      className="h-64"
    />
  )

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-3.5 border-b border-solid border-neutral-200 bg-white px-4 pb-6 pt-4">
        <SizingIcon />
        <header className="mr-auto space-y-1">
          <div className="flex items-center gap-2">
            <h1 className="font-[Roobert] text-lg font-medium leading-6 text-neutral-900">
              {t('sizing.title')}
            </h1>
            <SizingInfoButton />
          </div>
          <h2 className="font-[Roobert] text-sm font-normal leading-5 text-neutral-600">
            {t('sizing.subtitle')}
          </h2>
        </header>
      </div>

      <TabsRoot
        value={currentTab}
        onValueChange={(value: TabsValue) => {
          if (value === OVERVIEW) {
            searchParams.delete('tab')
          } else {
            searchParams.set('tab', value)
          }
          setSearchParams(searchParams)
        }}
      >
        <TabsList className="bg-white pl-4 pt-3 shadow-none">
          {/* El indicador del DS va a un top fijo: súmale el pt de la lista. */}
          <TabsIndicator className="top-[3.21rem]" />
          <Tab value={OVERVIEW}>{t('sizing.tabs.overview')}</Tab>
          <Tab value={MODELS}>
            {t('sizing.tabs.models')}
            {modelCount > 0 ? (
              <Badge variant="count">{modelCount}</Badge>
            ) : null}
          </Tab>
        </TabsList>

        <div className="grow overflow-y-auto p-4">
          <TabPanel value={OVERVIEW}>
            {overviewStatus === 'pending' ? (
              <OverviewTabLoading />
            ) : overviewStatus === 'error' ? (
              loadError
            ) : (
              <OverviewTab overview={overview} results={cycle?.results ?? []} />
            )}
          </TabPanel>
          <TabPanel value={MODELS}>
            {modelsStatus === 'pending' ? (
              <OverviewTabLoading />
            ) : modelsStatus === 'error' ? (
              loadError
            ) : (
              <ModelsTab data={models} />
            )}
          </TabPanel>
        </div>
      </TabsRoot>
    </div>
  )
}

export default SizingPage
